import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EmptyState } from '../../components/EmptyState';
import { PageHeader } from '../../components/PageHeader';
import { Button } from '../../components/Button';
import { ErrorNotice } from '../../components/ErrorNotice';
import { TextField } from '../../components/Field';
import { LoadingIndicator } from '../../components/Spinner';
import type { CategoryTree } from './api';
import { useCategories, useCreateCategory, useDeleteCategory, useUpdateCategory } from './queries';

/** Hoechstens drei Ebenen -- docs/06, F1-06. */
const MAX_DEPTH = 3;

/**
 * Der Kategoriebaum.
 *
 * Der Baum kommt fertig verschachtelt vom Server; die Oberflaeche stellt ihn
 * nur dar. Das ist Absicht: Agentenoberflaeche und Portal wuerden ihn sonst
 * beide zusammensetzen, und zwei Umsetzungen desselben Baumaufbaus laufen
 * auseinander.
 */
export function CategoryPage() {
  const { t } = useTranslation();
  const categories = useCategories();
  const create = useCreateCategory();
  const remove = useDeleteCategory();
  const rename = useUpdateCategory();
  const [rootName, setRootName] = useState('');

  return (
    <>
      <PageHeader title={t('categories.title')} subtitle={t('categories.subtitle')} />

      {categories.isError ? <ErrorNotice error={categories.error} /> : null}
      {create.isError ? <ErrorNotice error={create.error} /> : null}
      {remove.isError ? <ErrorNotice error={remove.error} /> : null}

      <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
        <form
          className="row"
          style={{ alignItems: 'flex-end' }}
          onSubmit={(event) => {
            event.preventDefault();
            if (!rootName.trim()) return;
            create.mutate(
              { name: rootName.trim(), parent_id: null },
              {
                onSuccess: () => {
                  setRootName('');
                },
              },
            );
          }}
        >
          <div style={{ flex: 1, minWidth: '14rem' }}>
            <TextField
              label={t('categories.addRoot')}
              placeholder={t('categories.namePlaceholder')}
              value={rootName}
              onChange={(event) => {
                setRootName(event.target.value);
              }}
            />
          </div>
          <div style={{ marginBottom: 'var(--space-4)' }}>
            <Button type="submit" variant="primary" busy={create.isPending}>
              {t('masterdata.add')}
            </Button>
          </div>
        </form>
      </div>

      <div className="card">
        {categories.isPending ? <LoadingIndicator /> : null}

        {categories.data?.length === 0 ? (
          <EmptyState
            icon="categories"
            title={t('categories.empty')}
            hint={t('categories.emptyHint')}
          />
        ) : null}

        {categories.data?.map((node) => (
          <CategoryNode
            key={node.id}
            node={node}
            onCreate={(name, parentId) => {
              create.mutate({ name, parent_id: parentId });
            }}
            onDelete={(id) => {
              remove.mutate(id);
            }}
            onRename={(id, name) => {
              rename.mutate({ id, payload: { name } });
            }}
            busy={create.isPending || remove.isPending}
          />
        ))}
      </div>
    </>
  );
}

interface CategoryNodeProps {
  node: CategoryTree;
  onCreate: (name: string, parentId: string) => void;
  onDelete: (id: string) => void;
  onRename: (id: string, name: string) => void;
  busy: boolean;
}

function CategoryNode({ node, onCreate, onDelete, onRename, busy }: CategoryNodeProps) {
  const { t } = useTranslation();
  const [childName, setChildName] = useState('');
  const [adding, setAdding] = useState(false);

  // Umbenennen statt loeschen und neu anlegen: An einer Kategorie haengen
  // Tickets und Wissensartikel. Wer sie wegwirft, um sie anders zu nennen,
  // nimmt der Auswertung ihre Vorgeschichte.
  const [renaming, setRenaming] = useState<string | null>(null);

  const canHaveChildren = node.depth + 1 < MAX_DEPTH;

  return (
    <div
      style={{
        paddingLeft: `calc(${node.depth} * var(--space-6))`,
        borderLeft: node.depth > 0 ? '1px solid var(--color-border-subtle)' : undefined,
      }}
    >
      <div
        className="row"
        style={{
          justifyContent: 'space-between',
          padding: 'var(--space-2) 0',
          borderBottom: '1px solid var(--color-border-subtle)',
        }}
      >
        {renaming === null ? (
          <span style={{ fontWeight: node.depth === 0 ? 'var(--font-weight-medium)' : undefined }}>
            {node.name}
          </span>
        ) : (
          <input
            className="field__control"
            aria-label={t('categories.renameTo')}
            value={renaming}
            onChange={(event) => {
              setRenaming(event.target.value);
            }}
          />
        )}
        <div className="row">
          {renaming === null ? (
            <Button
              variant="ghost"
              onClick={() => {
                setRenaming(node.name);
              }}
            >
              {t('masterdata.edit')}
            </Button>
          ) : (
            <>
              <Button
                variant="ghost"
                busy={busy}
                disabled={renaming.trim() === ''}
                onClick={() => {
                  onRename(node.id, renaming.trim());
                  setRenaming(null);
                }}
              >
                {t('app.save')}
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  setRenaming(null);
                }}
              >
                {t('app.cancel')}
              </Button>
            </>
          )}
          {canHaveChildren ? (
            <Button
              variant="ghost"
              onClick={() => {
                setAdding((current) => !current);
              }}
            >
              {t('categories.addChild')}
            </Button>
          ) : null}
          <Button
            variant="ghost"
            busy={busy}
            onClick={() => {
              if (window.confirm(t('masterdata.deleteConfirm'))) {
                onDelete(node.id);
              }
            }}
          >
            {t('masterdata.delete')}
          </Button>
        </div>
      </div>

      {adding ? (
        <form
          className="row"
          style={{ padding: 'var(--space-2) 0' }}
          onSubmit={(event) => {
            event.preventDefault();
            if (!childName.trim()) return;
            onCreate(childName.trim(), node.id);
            setChildName('');
            setAdding(false);
          }}
        >
          <input
            className="field__control"
            placeholder={t('categories.namePlaceholder')}
            value={childName}
            autoFocus
            onChange={(event) => {
              setChildName(event.target.value);
            }}
          />
          <Button type="submit" variant="secondary">
            {t('masterdata.add')}
          </Button>
        </form>
      ) : null}

      {/* ``children`` ist im Schema wahlfrei, weil es eine Vorgabe hat --
          ein Blatt liefert eine leere Liste. */}
      {(node.children ?? []).map((child) => (
        <CategoryNode
          key={child.id}
          node={child}
          onCreate={onCreate}
          onDelete={onDelete}
          onRename={onRename}
          busy={busy}
        />
      ))}
    </div>
  );
}
